import * as React from "react";
import { Button, CloseButton, Form, Modal, Spinner } from "react-bootstrap";
import {
  IAddUserGuidePayload,
  IEditUserGuidePayload,
  IUserGuide,
  UserGuideModalMode,
} from "../userGuideTypes";
import {
  ACCEPTED_USER_GUIDE_FILE_EXTENSIONS,
  USER_GUIDE_TITLE_MAX_LENGTH,
  validateUserGuideFile,
} from "../utils";

interface IUserGuideModalProps {
  show: boolean;
  mode: UserGuideModalMode;
  selectedGuide: IUserGuide | null;
  userGuides: IUserGuide[];
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (
    payload: IAddUserGuidePayload | IEditUserGuidePayload,
  ) => Promise<void>;
}

const UserGuideModal = ({
  show,
  mode,
  selectedGuide,
  userGuides,
  isSubmitting,
  onClose,
  onSubmit,
}: IUserGuideModalProps) => {
  const [title, setTitle] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [titleError, setTitleError] = React.useState("");
  const [fileError, setFileError] = React.useState("");

  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const isEditMode = mode === "edit";

  React.useEffect(() => {
    if (!show) {
      return;
    }

    setTitle(selectedGuide?.title || "");
    setDescription(selectedGuide?.description || "");
    setSelectedFile(null);
    setTitleError("");
    setFileError("");
  }, [show, selectedGuide]);

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const file = event.target.files?.[0] || null;

    setSelectedFile(file);

    if (!file) {
      setFileError("");
      return;
    }

    setFileError(validateUserGuideFile(file));
  };

  const handleClearSelectedFile = (): void => {
    setSelectedFile(null);
    setFileError("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const validateForm = (): boolean => {
    let isValid = true;
    const trimmedTitle = title.trim();

    setTitleError("");
    setFileError("");

    if (!trimmedTitle) {
      setTitleError("Title is required.");
      isValid = false;
    } else if (trimmedTitle.length > USER_GUIDE_TITLE_MAX_LENGTH) {
      setTitleError(
        `Title should not exceed ${USER_GUIDE_TITLE_MAX_LENGTH} characters.`,
      );
      isValid = false;
    } else {
      const normalizedTitle = trimmedTitle.toLowerCase();

      const isDuplicateTitle = userGuides.some((guide) => {
        const existingTitle = guide.title?.trim().toLowerCase();

        return (
          existingTitle === normalizedTitle && guide.id !== selectedGuide?.id
        );
      });

      if (isDuplicateTitle) {
        setTitleError("Guide title already exists.");
        isValid = false;
      }
    }

    if (!isEditMode) {
      const fileValidationMessage = validateUserGuideFile(selectedFile);

      if (fileValidationMessage) {
        setFileError(fileValidationMessage);
        isValid = false;
      }
    } else if (selectedFile) {
      const fileValidationMessage = validateUserGuideFile(selectedFile);

      if (fileValidationMessage) {
        setFileError(fileValidationMessage);
        isValid = false;
      }
    }

    return isValid;
  };

  const isSubmitDisabled =
    isSubmitting || Boolean(titleError) || Boolean(fileError);

  const handleSubmit = async (): Promise<void> => {
    if (!validateForm()) {
      return;
    }

    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();

    if (isEditMode) {
      if (!selectedGuide?.id) {
        return;
      }

      await onSubmit({
        guideId: selectedGuide.id,
        title: trimmedTitle,
        description: trimmedDescription,
        file: selectedFile,
      });

      return;
    }

    if (!selectedFile) {
      setFileError("File is required.");
      return;
    }

    await onSubmit({
      title: trimmedTitle,
      description: trimmedDescription,
      file: selectedFile,
    });
  };

  return (
    <Modal
      show={show}
      onHide={isSubmitting ? undefined : onClose}
      centered
      className="user-guide-modal"
    >
      <Modal.Header closeButton={!isSubmitting}>
        <Modal.Title>
          {isEditMode ? "Edit Help Guide" : "Add Help Guide"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form.Group className="user-guide-form-group">
          <Form.Label>
            Title <span className="required-mark">*</span>
          </Form.Label>
          <Form.Control
            type="text"
            value={title}
            maxLength={USER_GUIDE_TITLE_MAX_LENGTH}
            placeholder="Enter title"
            disabled={isSubmitting}
            onChange={(event) => {
              setTitle(event.target.value);
              setTitleError("");
            }}
          />
          {titleError && <div className="user-guide-error">{titleError}</div>}
        </Form.Group>

        <Form.Group className="user-guide-form-group">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={description}
            placeholder="Enter description"
            disabled={isSubmitting}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Form.Group>

        <Form.Group className="user-guide-form-group">
          <Form.Label>
            File {!isEditMode && <span className="required-mark">*</span>}
          </Form.Label>

          <div className="user-guide-file-input-wrapper">
            <Form.Control
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_USER_GUIDE_FILE_EXTENSIONS}
              disabled={isSubmitting}
              onChange={handleFileChange}
            />

            {selectedFile && (
              <CloseButton
                disabled={isSubmitting}
                className="user-guide-clear-file-button"
                aria-label="Remove selected file"
                onClick={handleClearSelectedFile}
              />
            )}
          </div>

          <div className="user-guide-file-hint">
            {isEditMode
              ? "Upload a new file only if you want to replace the existing file."
              : "Allowed files: PDF, DOC, DOCX, PPT, PPTX, MP4, MPEG, MOV, AVI."}
          </div>

          {fileError && <div className="user-guide-error">{fileError}</div>}
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="light"
          disabled={isSubmitting}
          onClick={onClose}
          className="user-guide-cancel-button"
        >
          Cancel
        </Button>

        <Button
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          className="user-guide-submit-button"
        >
          {isSubmitting && <Spinner animation="border" size="sm" />}
          {isEditMode ? "Update" : "Add"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UserGuideModal;
